import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { useDebounce } from 'use-debounce'

import {
  ProductCardTypes,
  ProductCardsType,
  TestPrepProductAPIStat,
  useProductStatsQuery,
} from '~/redux/apis/user-stats-api'
import { useAppSelector } from '~/redux/hooks'
import {
  selectIsProductCardTimeFilterSetToAllTime,
  selectProductCardFilterOptions,
  selectProductCardTimeFilter,
} from '~/statistics/redux/statistics.slice'
import { DayOfMonth } from '~/statistics/types/common.types'
import { getFilterDates, getLastVisitDate } from '~/statistics/utils/get-filter-date.utils'
import { groupProductDataByDate } from '~/statistics/utils/group-product-data.utils'
import { convertMsToDayHourMinute } from '~/user-stats/utility/date.utils'
import { mappingProductCardFilterOptionsWithType } from '~/statistics/utils/common.utils'
import { PRODUCT_CARD_NOT_APPLICABLE_DATE } from '~/statistics/constants'
import LoaderSkeleton from '~/shared/components/LoaderSkeleton'

import BootcampProductStatCard from './Bootcamp/BootcampProductStatCard'
import StatsNotFound from './Bootcamp/StatsNotFound'
import MathVaultProductCard from './MathVault/MathVaultCard'
import MathEssentialCard from './MathEssential/MathEssentialCard'
import StackNLoadProductCard from './StackNLoad/StackNLoadCard'
import TreasureTrailProductCard from './TreasureTrail/TreasureTrailCard'
import MultGameProductCard from './MultGame/MultGameCard'

import WindSurferProductCard from './WindSurfer/WindSurferProductCard'
import CurriculumArticleProductStatCard from './CurriculumArticle/StatCard'
import CurriculumVideoProductStatCard from './CurriculumVideo/StatCard'
import TestPrepProductCard from './TestPrep/TestPrepProductCard'

export default function ProductCardsContainer({ familyMemberId }: { familyMemberId?: number }): React.ReactElement {
  const { t } = useTranslation('statistics')
  const { locale } = useRouter()

  const productCategoryFilter = useAppSelector(selectProductCardTimeFilter)
  const isProductCardTimeFilterSetToAllTime = useAppSelector(selectIsProductCardTimeFilterSetToAllTime)

  // Get selected product card options from redux store
  const filterOptions = mappingProductCardFilterOptionsWithType(useAppSelector(selectProductCardFilterOptions))

  const [startDate, endDate] = useMemo(() => {
    return getFilterDates(productCategoryFilter)
  }, [productCategoryFilter])

  const filter = useMemo(() => {
    return {
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      locale,
    }
  }, [endDate, locale, startDate])

  const [debouncedFilter] = useDebounce(filter, 500)

  const {
    data: productStats,
    isLoading: isProductsStatsLoading,
    isUninitialized,
  } = useProductStatsQuery({ ...debouncedFilter, familyMemberId })

  const mapProductsByDate = useMemo(() => {
    let filteredProductStats = productStats || []
    if (filterOptions?.length) {
      filteredProductStats = productStats?.filter((product) => filterOptions.includes(product.type)) || []
    }

    return groupProductDataByDate(filteredProductStats)
  }, [productStats, filterOptions])

  if (!isUninitialized && !isProductsStatsLoading && !Object.entries(mapProductsByDate).length) {
    if (isProductCardTimeFilterSetToAllTime) {
      return <StatsNotFound />
    } else {
      const { days: filteredDays } = convertMsToDayHourMinute(
        new Date(endDate?.toISOString() as string).getTime() - new Date(startDate?.toISOString() as string).getTime(),
      )

      return <StatsNotFound days={filteredDays} />
    }
  }

  return (
    <div>
      {isProductsStatsLoading && <LoaderSkeleton className="h-28 w-full rounded-2.5 my-4" rows={3} />}
      {Object.entries(mapProductsByDate).map(([date, productList]) => {
        const lastVisitedDate = date !== PRODUCT_CARD_NOT_APPLICABLE_DATE ? getLastVisitDate(date) : null
        const formattedDate =
          lastVisitedDate === DayOfMonth.TODAY || lastVisitedDate === DayOfMonth.YESTERDAY
            ? t(lastVisitedDate)
            : lastVisitedDate

        return (
          <div className="mb-4" key={date}>
            {formattedDate && (
              <p className="pl-1 font-medium text-xs text-white">
                {t('last-visited')}: {formattedDate}
              </p>
            )}
            {productList.map((product: ProductCardsType) => {
              switch (product.type) {
                case ProductCardTypes.BOOTCAMP:
                  return <BootcampProductStatCard product={product} />
                case ProductCardTypes.CURRICULUM_ARTICLE:
                  return <CurriculumArticleProductStatCard product={product} />
                case ProductCardTypes.CURRICULUM_VIDEO:
                  return <CurriculumVideoProductStatCard product={product} />
                case ProductCardTypes.MATHVAULT:
                  return <MathVaultProductCard product={product} />
                case ProductCardTypes.MATHESSENTIAL:
                  return <MathEssentialCard product={product} />
                case ProductCardTypes.STACKNLOAD:
                  return <StackNLoadProductCard product={product} />
                case ProductCardTypes.TREAURETRAIL:
                  return <TreasureTrailProductCard product={product} />
                case ProductCardTypes.WINDSURFER:
                  return <WindSurferProductCard product={product} />
                case ProductCardTypes.MULT_GAME:
                  return <MultGameProductCard product={product} />
                case ProductCardTypes.TEST_PREP:
                  return <TestPrepProductCard product={product as TestPrepProductAPIStat} />
                default:
                  return <></>
              }
            })}
          </div>
        )
      })}
    </div>
  )
}
